import React, { useState, useEffect } from "react"
import { Stack, Container, Button, Typography, Grid, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Box } from "@mui/material";
import { DndContext } from '@dnd-kit/core';
import { pdf } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import CreatePlanDropdown from "../components/plans/CreatePlanDropdown";
import usePlanStore from "../store/planStore"
import Snackbar from "@mui/material/Snackbar";
import WeeklyPlanGrid from "../components/plans/WeeklyPlanGrid";
import EventPlanList from "../components/plans/EventPlanList";
import DishSearchPanel from "../components/plans/DishSearchPanel";
import PlanList from "../components/plans/PlanList";
import AddFab from "../components/common/AddFab";
import useDishDialog from "../hooks/useDishDialog";
import DishEditDialog from "../components/dishes/DishEditDialog";
import GroceryList from "../components/shopping/GroceryList";

const PlanPage = () => {
  const { t } = useTranslation();
  const [selectedDay, setSelectedDay] = useState('monday');
  const [weeklyPlan, setWeeklyPlan] = useState({});
  const [weekRange, setWeekRange] = useState({ startOfWeek: '', endOfWeek: '' });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Use the custom dish dialog hook
  const {
    dialogOpen,
    selectedDish,
    handleDishClick,
    handleDialogClose,
    handleAddNewDish,
    handleDishSave
  } = useDishDialog();

  const {
    currentPlan,
    loading,
    error,
    fetchPlan,
    fetchPlans,
    randomizePlan,
    addDishToPlan,
    deleteDishFromPlan,
    deleteDishFromEventPlan,
    moveDishInPlan,
    updatePlanDay,
    updateEventPlan,
    deletePlan,
    createPlan,
    selectedPlanId,
  } = usePlanStore();

  const handleWeekChange = (range) => {
    setWeekRange(range);
  };

  const handleGeneratePlan = async (mealPreferences = null) => {
    if (weekRange.startOfWeek === '') {
      setSnackbarMessage(t('plans.selectDate'));
      setSnackbarOpen(true);
    } else {
      // Ensure mealPreferences is properly structured or null
      const validMealPreferences = mealPreferences && 
        mealPreferences.breakfast && 
        mealPreferences.lunch && 
        mealPreferences.dinner 
        ? mealPreferences 
        : null;
      
      const data = await randomizePlan(
        weekRange.startOfWeek, 
        weekRange.endOfWeek, 
        validMealPreferences, 
        14 // rotation days
      );
    }
  };

  const handleSavePlan = async (mode, eventData = null) => {
    try {
      if (eventData || (currentPlan && currentPlan.mode === 'event')) {
        // Creating/updating an event plan
        if (mode === "new") {
          // Create new event plan
          // TODO: Implement event plan creation in store
          console.log('Creating event plan:', eventData);
          
          await createPlan(
            eventData.date,
            eventData.date,
            eventData,
            'Event'
          );
          await fetchPlans();          
          setSnackbarMessage(t('plans.eventCreated'));
          setSnackbarOpen(true);
        } else if (mode === "update") {
          // Update existing event plan
          if (!selectedPlanId) {
            setSnackbarMessage(t('plans.noDataSelected'));
            setSnackbarOpen(true);
            return;
          }
          
          const eventUpdateData = {
            event_name: currentPlan.title || 'Event Plan',
            event_date: currentPlan.date || new Date().toISOString().split('T')[0],
            guests: currentPlan.guest_count || currentPlan.guestCount || 1,
            description: currentPlan.description || '',
            plans: currentPlan.items || []
          };
          
          await updateEventPlan(selectedPlanId, eventUpdateData);
          setSnackbarMessage(t('plans.eventUpdated'));
          setSnackbarOpen(true);
          console.log('Event plan updated');
        }
      } else {
        // Creating/updating a weekly plan
        if (!currentPlan || !currentPlan.plans) {
          setSnackbarMessage(t('plans.nothingToSave'));
          setSnackbarOpen(true);
          return;
        }

        if (mode === "update") {
          const planId = selectedPlanId;
          await updatePlanDay(
            planId,
            weekRange.startOfWeek,
            weekRange.endOfWeek,
            currentPlan.plans,
          )
          setSnackbarMessage(t('plans.planUpdated'));
          setSnackbarOpen(true);
          console.log('Update plan')
        } else if (mode === "new") {
          await createPlan(
            weekRange.startOfWeek,
            weekRange.endOfWeek,
            currentPlan.plans,
            'Weekly',
          );
          await fetchPlans();

          setSnackbarMessage(t('plans.planSaved'));
          setSnackbarOpen(true);
        }
      }
    } catch (err) {
      console.error('Save plan failed', err);
      setSnackbarMessage('Failed to save plan: ' + (err?.message || 'Unknown error'));
      setSnackbarOpen(true);
    }
  }

  const handleDeletePlanConfirm = async () => {
    if (!selectedPlanId) {
      setSnackbarMessage(t('plans.noDataSelected'));
      setSnackbarOpen(true);
      return;
    }

    try {
      await deletePlan(selectedPlanId);

      usePlanStore.setState({
        currentPlan: null,
        selectedPlanId: '',
      });

      await fetchPlans();

      setSnackbarMessage(t('plans.planDeleted'));
      setSnackbarOpen(true);

      setWeekRange({ startOfWeek: '', endOfWeek: '' });

    } catch (err) {
      console.error('Delete plan failed', err);
      setSnackbarMessage(t('common.messages.error') + ': ' + (err?.message || 'Unknown error'));
      setSnackbarOpen(true);
    } finally {
      setDeleteDialogOpen(false);
    }
  }

  const handleDeletePlan = () => {
    if (!selectedPlanId) {
      setSnackbarMessage(t('plans.noDataSelected'));
      setSnackbarOpen(true);
      return;
    }

    setDeleteDialogOpen(true);
  }

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleGroceryList = async () => {
    if (!currentPlan || !currentPlan.plans) {
      setSnackbarMessage(t('plans.noPlanForGrocery'));
      setSnackbarOpen(true);
      return;
    }

    try {
      // Generate PDF blob
      const pdfBlob = await pdf(<GroceryList planData={currentPlan} weekRange={weekRange} />).toBlob();

      // Create blob URL
      const blobUrl = URL.createObjectURL(pdfBlob);

      // Open in new tab
      window.open(blobUrl, '_blank');

      // Clean up the URL after a short delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);

      setSnackbarMessage(t('plans.groceryListGenerated'));
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error generating grocery list:', error);
      setSnackbarMessage(t('plans.groceryListFailed'));
      setSnackbarOpen(true);
    }
  };

  // Enhanced drag end handler
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    const dishData = active.data.current;
    const dropData = over.data.current;

    console.log('Dragged:', dishData);
    console.log('Dropped on:', dropData);

    if (dishData?.type === 'dish' && dropData?.type === 'meal') {
      addDishToPlan(dropData.day, dropData.date, dropData.mealTime, dishData.dish);
      console.log(`Adding ${dishData.dish.name} to ${dropData.day} ${dropData.mealTime}`);
    } else if (dishData?.type === 'restaurant' && dropData?.type === 'meal') {
      addDishToPlan(dropData.day, dropData.date, dropData.mealTime, dishData.restaurant);
      console.log(`Adding ${dishData.restaurant.name} to ${dropData.day} ${dropData.mealTime}`);
    } else if (dishData?.type === 'dish' && dropData?.type === 'event') {
      // Adding dish to event plan
      addDishToPlan(null, null, null, dishData.dish);
      // console.log(`Adding ${dishData.dish.name} to event plan`);
      // setSnackbarMessage(`Added ${dishData.dish.name} to event plan`);
      // setSnackbarOpen(true);
    } else if (dishData?.type === 'restaurant' && dropData?.type === 'event') {
      // Adding restaurant to event plan
      addDishToPlan(null, null, null, dishData.restaurant);
      // console.log(`Adding ${dishData.restaurant.name} to event plan`);
      // setSnackbarMessage(`Added ${dishData.restaurant.name} to event plan`);
      // setSnackbarOpen(true);
    } else if (dishData?.type === 'planDish' && dropData?.type === 'meal') {
      const { fromDay, fromMeal, dishIndex } = dishData;
      const { day: toDay, mealTime: toMeal } = dropData;

      console.log(`Moving dish from ${fromDay} ${fromMeal} to ${toDay} ${toMeal}`);
      moveDishInPlan(fromDay, fromMeal, toDay, toMeal, dishIndex);
    } else if (dishData?.type === 'eventDish' && dropData?.type === 'meal') {
      // Moving dish from event to weekly plan
      const { dish, dishIndex } = dishData;
      const { day, date, mealTime } = dropData;
      console.log(`Moving ${dish.name} from event to ${day} ${mealTime}`);
      addDishToPlan(day, date, mealTime, dish);
      deleteDishFromEventPlan(dishIndex);
      setSnackbarMessage(`Moved ${dish.name} to ${day} ${mealTime}`);
      setSnackbarOpen(true);
    } else if (dishData?.type === 'eventDish' && dropData?.type === 'delete') {
      // Deleting dish from event plan
      const { dishIndex } = dishData;
      console.log(`Deleting dish from event plan at index ${dishIndex}`);
      deleteDishFromEventPlan(dishIndex);
      setSnackbarMessage('Dish removed from event plan');
      setSnackbarOpen(true);
    } else if (dishData?.type === 'planDish' && dropData?.type === 'delete') {
      const { fromDay, fromMeal, dishIndex } = dishData;
      console.log(`Deleting dish from ${fromDay} ${fromMeal} at index ${dishIndex}`);
      deleteDishFromPlan(fromDay, fromMeal, dishIndex);
    } else {
      console.error('Not a valid operation')
    }
  };

  if (loading) return <div>{t('plans.loadingPlan')}</div>;
  if (error) return <div>{t('common.messages.error')}: {error}</div>;

  return (
    <Container maxWidth="lg">


      {/* Plan management section */}
      <Box
        justifyContent="center"
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        m={3}
      >
        {/* New consolidated Create Plan dropdown */}
        <CreatePlanDropdown
          onWeekChange={handleWeekChange}
          weekRange={weekRange}
          onGeneratePlan={handleGeneratePlan}
          onSavePlan={handleSavePlan}
        />

        {/* Existing plan management buttons */}
        <Button
          variant="outlined"
          onClick={() => handleSavePlan("update")}
          disabled={!selectedPlanId}
          sx={{ textTransform: "none" }}
        >
          {t('plans.updatePlan')}
        </Button>

        <Button
          variant="outlined"
          color="error"
          onClick={handleDeletePlan}
          disabled={!selectedPlanId}
          sx={{ textTransform: "none" }}
        >
          {t('plans.deletePlan')}
        </Button>


        <Button
          variant="outlined"
          onClick={handleGroceryList}
          disabled={!selectedPlanId}
          sx={{ textTransform: "none" }}
        >
          {t('plans.groceryList')}
        </Button>

        <PlanList onPlanSelect={handleWeekChange} />

      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />

      {/* Conditional plan view based on plan type */}
      <DndContext onDragEnd={handleDragEnd}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={9}>
            {currentPlan && currentPlan.mode === 'event' ? (
              <EventPlanList
                plan={currentPlan}
              />
            ) : (
              <WeeklyPlanGrid
                plan={currentPlan}
                onDeleteDish={deleteDishFromPlan}
              />
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            <DishSearchPanel />
          </Grid>
        </Grid>
      </DndContext>

      <AddFab
        onClick={handleAddNewDish}
        sx={{
          bottom: 80,
          right: 16,
        }}
      />

      <DishEditDialog
        open={dialogOpen}
        dish={selectedDish}
        onClose={handleDialogClose}
        onSave={handleDishSave}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('plans.deleteDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('plans.confirmDeletePlan')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.buttons.cancel')}</Button>
          <Button
            onClick={handleDeletePlanConfirm}
            color="error"
            autoFocus
          >
            {t('common.buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlanPage;